import Image from 'next/image'
import Link from 'next/link'
import React, { useEffect, useState } from 'react'
import { IDiamond } from 'types/diamond.types'
import icoDiamond from '@publicAssets/images/ui/step/ico-diamond.svg'
import step1 from '@publicAssets/images/ui/step/step-1.svg'
import step2 from '@publicAssets/images/ui/step/step-2.svg'
import { useRouter } from 'next/router'
import { formatUSDollar } from '@utils/format-price'
import sampleDiamondImages from '@components/yadav/ui/DiamondViewSlider/images.json'
import { YadavDiamond } from '@commerce/types/diamond'

const StepDiamond: React.FC<{
  active?: boolean
  choosing: boolean
  chosen: boolean
  diamondData?: IDiamond
  startWith?: 'setting' | 'diamond' | string
}> = ({ active, choosing, chosen, diamondData, startWith }) => {
  const [isChosen, setIsChosen] = useState(true)
  const [linkDiamond, setLinkDiamond] = useState('/natural-loose-diamonds/')
  const [viewDiamond, setViewDiamond] = useState('')
  const placeholderImg = '/product-img-placeholder.svg'
  const router = useRouter()
  const { pathname } = useRouter()
  let myPath: String[] = pathname.split('/')

  const handleDeleteDiamond = () => {
    if (typeof window !== 'undefined') {
      try {
        const storedDiamond = window.localStorage.getItem('diamond')
        const storedModifiedCombination = window.localStorage.getItem(
          'modified-combination'
        )
        if (storedDiamond && storedModifiedCombination) {
          const diamond = JSON.parse(storedDiamond) as YadavDiamond
          const modifiedCombination: {
            itemId: string | number
            groupId: number
          } = JSON.parse(storedModifiedCombination)
          const { itemId } = modifiedCombination

          if (!itemId) {
            window.localStorage.removeItem('modified-combination')
          }
          // Remove the stored modified-combination in case the customer is reassembling a setting-diamond combination but wants to change the diamond.
          if (diamond.id == itemId) {
            window.localStorage.removeItem('modified-combination')
          }
          window.localStorage.removeItem('diamond')
        } else {
          window.localStorage.removeItem('diamond')
          window.localStorage.removeItem('modified-combination')
        }
      } catch (error) {
        window.localStorage.removeItem('diamond')
        window.localStorage.removeItem('modified-combination')
      }
    }
  }

  useEffect(() => {
    if (diamondData && diamondData.labcreated) {
      setLinkDiamond('/lab-created-diamonds')
    } else {
      setLinkDiamond('/natural-loose-diamonds')
    }
    if (diamondData) {
      setIsChosen(true)
    } else {
      setIsChosen(false)
    }
    if (diamondData && diamondData.id) {
      setViewDiamond(String(diamondData.id))
    }
  }, [diamondData])

  const isPage = () => {
    if (
      pathname.includes('/natural-loose-diamonds') ||
      pathname.includes('/lab-created-diamonds')
    ) {
      router.reload()
    } else if (diamondData?.labcreated) {
      router.push('/lab-created-diamonds')
    } else {
      router.push('/natural-loose-diamonds')
    }
  }

  const getDefaultShapeImg = (shape: string | undefined | null) => {
    if (!shape) {
      return undefined
    }

    const item = sampleDiamondImages.find(
      (i) => i.shape === shape?.toUpperCase()
    )
    return item?.shapeImg
  }

  return (
    <>
      {active ? (
        <div className="flex flex-1 flex-row justify-between bg-white">
          <div className="relative flex justify-center items-center w-full flex-row md:justify-around lg:px-4 lg:justify-between lg:gap-3 mmd:gap-2">
            {startWith && startWith === 'diamond' ? (
              <div className="md:w-[19px] md:h-[25px] w-[5px] h-4 relative">
                <Image
                  src={step1}
                  alt={'Step 1'}
                  objectFit="contain"
                  layout="fill"
                  className="absolute top-[-20px] md:top-0"
                />
              </div>
            ) : (
              <div className="md:w-[19px] md:h-[25px] w-[9px] h-4 relative">
                <Image
                  src={step2}
                  alt={'Step 2'}
                  objectFit="contain"
                  layout="fill"
                  className="absolute top-[-20px] md:top-0"
                />
              </div>
            )}
            <div className="flex md:flex-col flex-row items-start">
              {diamondData && diamondData.id ? (
                <Link href={linkDiamond}>
                  <a className="inline-flex flex-col">
                    <h2 className="capitalize text-xs md:text-base hover:underline font-semibold text-gray-dark leading-5">
                      Diamond
                    </h2>
                  </a>
                </Link>
              ) : (
                <Link href={linkDiamond}>
                  <a className="inline-flex flex-col">
                    <h2 className="capitalize text-xs md:text-base hover:underline font-semibold text-gray-dark leading-5">
                      Diamond
                    </h2>
                    <p className="text-xs font-normal mlg:hidden text-gray-dark mmd:hidden">
                      Choose your Diamond
                    </p>
                  </a>
                </Link>
              )}
            </div>
            {choosing ? (
              <div className="flex flex-col items-center gap-2 md:flex-row-reverse mmd:hidden">
                <Link href={linkDiamond}>
                  <a className="inline-flex msm:mt-1">
                    <Image
                      src={icoDiamond}
                      alt={'Diamond activ icon'}
                      width={30}
                      height={25}
                    />
                  </a>
                </Link>
                {myPath.includes('[slug]') && (
                  <Link href={linkDiamond}>
                    <a className="text-xs text-gray-dark hover:underline md:text-sm md:relative">
                      Change
                    </a>
                  </Link>
                )}
              </div>
            ) : (
              <>
                {isChosen ? (
                  <div className="flex flex-col-reverse items-center md:flex-row md:gap-4 mmd:hidden">
                    <div className="flex flex-col items-end">
                      <div className="hidden text-sm text-gray-dark font-semibold md:block">
                        {diamondData && diamondData.price && (
                          <p>{formatUSDollar(Number(diamondData.price))}</p>
                        )}
                      </div>
                      <div className="flex flex-row text-xs">
                        <Link href={viewDiamond ? `/diamond/${viewDiamond}` : linkDiamond}>
                          <a className="hidden text-xs text-gray-dark hover:underline md:flex">
                            View
                          </a>
                        </Link>
                        <span className="hidden text-xs text-gray-dark hover:underline md:block ml-1">
                          |
                        </span>
                        <Link href={linkDiamond}>
                          <a
                            className="text-xs ml-[2px] text-gray-dark hover:underline"
                            onClick={() => {
                              handleDeleteDiamond()
                              isPage()
                            }}
                          >
                            Change
                          </a>
                        </Link>
                      </div>
                    </div>
                    <Link
                      href={
                        viewDiamond ? `/diamond/${viewDiamond}` : linkDiamond
                      }
                    >
                      <a className="relative block w-8 h-8">
                        <Image
                          src={
                            diamondData?.photourl1_local
                              ? diamondData.photourl1_local
                              : getDefaultShapeImg(
                                  diamondData?.shape as string
                                ) || placeholderImg
                          }
                          alt={'chosen diamond'}
                          layout="fill"
                          objectFit="cover"
                          className="rounded"
                        />
                      </a>
                    </Link>
                  </div>
                ) : (
                  <div className="msm:mt-1 mmd:hidden">
                    <Image
                      src={icoDiamond}
                      alt={'Diamond icon'}
                      width={30}
                      height={25}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-1 flex-row justify-between bg-gray-100">
          <div className="relative flex justify-center items-center w-full md:mb-0 flex-row md:justify-around lg:px-4 lg:justify-between lg:gap-4 mmd:gap-4">
            {startWith && startWith === 'diamond' ? (
              <div className="md:w-[19px] md:h-[25px] w-[5px] h-4 relative">
                <Image
                  src={step1}
                  alt={'Step 1'}
                  objectFit="contain"
                  layout="fill"
                  className="absolute top-[-20px] md:top-0"
                />
              </div>
            ) : (
              <div className="md:w-[19px] md:h-[25px] w-[9px] h-4 relative">
                <Image
                  src={step2}
                  alt={'Step 2'}
                  objectFit="contain"
                  layout="fill"
                  className="absolute top-[-20px] md:top-0"
                />
              </div>
            )}
            <div className="flex md:flex-col flex-row items-start ">
              {diamondData && diamondData.id ? (
                <Link href={linkDiamond}>
                  <a className="inline-flex flex-col">
                    <h2 className="capitalize text-xs md:text-base hover:underline font-semibold text-gray-dark leading-5">
                      Diamond
                    </h2>
                  </a>
                </Link>
              ) : (
                <Link href={linkDiamond}>
                  <a className="inline-flex flex-col">
                    <h2 className="capitalize text-xs md:text-base hover:underline font-semibold text-gray-dark leading-5">
                      Diamond
                    </h2>
                    <p className="text-xs font-normal mlg:hidden text-gray-dark mmd:hidden">
                      Choose your Diamond
                    </p>
                  </a>
                </Link>
              )}
            </div>
            {isChosen ? (
              <div className="flex flex-col-reverse items-center md:flex-row md:gap-4 mmd:hidden">
                <div className="flex flex-col items-end">
                  <div className="hidden text-sm text-gray-dark font-semibold md:block">
                    {diamondData && diamondData.price && (
                      <p>{formatUSDollar(Number(diamondData.price))}</p>
                    )}
                  </div>
                  <div className="flex flex-row text-xs">
                    <Link href={viewDiamond ? `/diamond/${viewDiamond}` : linkDiamond}>
                      <a className="hidden text-xs text-gray-dark hover:underline md:flex">
                        View
                      </a>
                    </Link>
                    <span className="hidden text-xs text-gray-dark hover:underline md:block ml-1">
                      |
                    </span>
                    <Link href={linkDiamond}>
                      <a
                        className="text-xs ml-[2px] text-gray-dark hover:underline"
                        onClick={handleDeleteDiamond}
                      >
                        Change
                      </a>
                    </Link>
                  </div>
                </div>
                <Link
                  href={viewDiamond ? `/diamond/${viewDiamond}` : linkDiamond}
                >
                  <a className="relative block w-8 h-8">
                    <Image
                      src={
                        diamondData?.photourl1_local
                          ? diamondData.photourl1_local
                          : getDefaultShapeImg(diamondData?.shape as string) ||
                            placeholderImg
                      }
                      alt={'chosen diamond'}
                      layout="fill"
                      objectFit="cover"
                      className="rounded"
                    />
                  </a>
                </Link>
              </div>
            ) : (
              <div className="mb-[11px] md:mb-0 flex items-center msm:mt-1 mmd:hidden">
                <Image
                  src={icoDiamond}
                  alt={'Diamond icon'}
                  width={36}
                  height={36}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
}

export default StepDiamond
